import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "@tanstack/react-router";

import PaywallModal from "components/paywall/PaywallModal";
import { FeatureType } from "components/paywall/paywallUtils";
import StrategyPaywallModal from "components/paywall/strategy_paywall/StrategyPaywallModal";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { ProDataTypeSeparateBuyersSuppliers } from "lib/types/models";
import { useCurrentUser } from "../hooks/api/users/useCurrentUser";
import { useSubscription } from "../hooks/subscription";

export function useConfirmUserDataSubscription(
  dataSubscription: ProDataTypeSeparateBuyersSuppliers,
  featureType: FeatureType,
): void {
  const { confirmUserDataSubscription } = usePaywall();
  useEffect(() => {
    confirmUserDataSubscription(dataSubscription, featureType, "/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export type OpenPaywallModalOptions = {
  trackingData?: EventData;
  contextSource?: string;
  requiredDataSubscription?: ProDataTypeSeparateBuyersSuppliers;
  onCloseRedirect?: string;
  strategyPaywall?: boolean;
  renderMedia?: boolean;
};

type PaywallState = {
  isVisible: boolean;
  onCloseRedirect?: string;
  featureType?: FeatureType;
  trackingData?: EventData;
  contextSource?: string;
  strategyPaywall?: boolean;
  renderMedia?: boolean;
};

type PaywallContextValue = PaywallState & {
  confirmUserDataSubscription: (
    requiredDataSubscription: ProDataTypeSeparateBuyersSuppliers,
    featureType?: FeatureType,
    onCloseRedirect?: string,
  ) => void;
  openPaywallModal: (featureType: FeatureType, options?: OpenPaywallModalOptions) => void;
};

const PaywallContext = React.createContext<PaywallContextValue | null>(null);

export function PaywallProvider({
  children,
  trackingPageName,
}: {
  children: React.ReactNode;
  trackingPageName?: string;
}): JSX.Element {
  const [state, setState] = useState<PaywallState>({
    isVisible: false,
    featureType: undefined,
  });
  const { hasDataTypes } = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const tracking = useTracking();
  // PaywallProvider is mounted both inside the router (migrated pages) and outside it
  // (legacy react_ujs pages, admin, Storybook). `useRouter({ warn: false })` returns the
  // router when present and null otherwise, so we SPA-navigate where we can and fall back
  // to a full-page redirect where there's no router — without warnings or throwing.
  const router = useRouter({ warn: false });

  const handleModalOpenChange = (open: boolean) => {
    if (open) {
      console.warn("onOpenChange with open=true is unsupported in Paywall");
      return;
    }
    // Hide the modal: with SPA navigation the provider stays mounted across the redirect,
    // so it won't unmount on its own like the old full-page reload did.
    setState((prevState) => ({ ...prevState, isVisible: false }));
    if (state.onCloseRedirect) {
      if (router) {
        // Assumes onCloseRedirect is an in-app SPA route (only "/" is passed today). A
        // Rails-only path or external URL would navigate to an unmatched route here rather
        // than doing a full page load.
        void router.navigate({ to: state.onCloseRedirect });
      } else {
        window.location.href = state.onCloseRedirect;
      }
    }
  };

  const openPaywallModal: PaywallContextValue["openPaywallModal"] = useCallback(
    (
      featureType,
      {
        requiredDataSubscription,
        trackingData,
        onCloseRedirect,
        contextSource,
        strategyPaywall,
        renderMedia,
      }: OpenPaywallModalOptions = {},
    ) => {
      tracking.logEvent(EventNames.paywallViewed, {
        "Paywall reason": "User not subscribed to data source",
        "Data source(s) required": requiredDataSubscription ?? "",
        "Feature type": featureType ?? "SUPPLIERS",
        "User's current data sources": currentUser?.dataTypeSubscriptions,
        "Page name": trackingPageName,
        "Context source": trackingData?.["Context source"] ?? "",
      });

      setState((prevState) => ({
        ...prevState,
        isVisible: true,
        featureType,
        onCloseRedirect,
        trackingData,
        contextSource,
        strategyPaywall,
        renderMedia,
      }));
    },
    [tracking, trackingPageName, currentUser],
  );

  const confirmUserDataSubscription: PaywallContextValue["confirmUserDataSubscription"] =
    useCallback(
      (
        requiredDataSubscription: ProDataTypeSeparateBuyersSuppliers,
        featureType?: FeatureType,
        onCloseRedirect?: string,
      ): void => {
        const isVisible = !hasDataTypes(requiredDataSubscription);

        if (isVisible) {
          return openPaywallModal(featureType ?? "SUPPLIERS", {
            requiredDataSubscription,
            onCloseRedirect,
          });
        }

        setState((prevState) => ({
          ...prevState,
          isVisible: false,
          featureType,
        }));
      },
      [hasDataTypes, openPaywallModal],
    );

  const value = useMemo(
    () => ({ ...state, confirmUserDataSubscription, openPaywallModal }),
    [confirmUserDataSubscription, openPaywallModal, state],
  );
  return (
    <PaywallContext.Provider value={value}>
      {children}
      {state.isVisible && (
        <>
          {state.strategyPaywall ? (
            <StrategyPaywallModal
              open={true}
              onOpenChange={handleModalOpenChange}
              featureType={state.featureType ?? "SUPPLIERS"}
              trackingData={state.trackingData}
              contextSource={state.contextSource}
            />
          ) : (
            <PaywallModal
              open={true}
              onOpenChange={handleModalOpenChange}
              featureType={state.featureType ?? "SUPPLIERS"}
              trackingData={state.trackingData}
              contextSource={state.contextSource}
              renderMedia={state.renderMedia}
            />
          )}
        </>
      )}
    </PaywallContext.Provider>
  );
}
export function usePaywall() {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error("usePaywall must be used within a PaywallProvider");
  }
  return context;
}
